import { motion } from "framer-motion";
import { Target, Eye, Heart, Users } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";

const team = [
  { name: "Ankit Verma", role: "CEO & Founder" },
  { name: "Priya Singh", role: "Head of Operations" },
  { name: "Rahul Mehta", role: "Tech Lead" },
  { name: "Sanya Kapoor", role: "Customer Success" },
];

export default function About() {
  return (
    <PublicLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#0A183D] to-[#1B3FA0] py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-3">Our Story</p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white uppercase leading-tight">
              About Sendtoo
            </h1>
            <p className="text-blue-200 mt-6 text-lg max-w-2xl mx-auto">
              Born from the belief that logistics should be simple, transparent, and reliable — Sendtoo is redefining how India ships.
            </p>
          </div>
        </div>

        {/* Mission & Vision */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: "Our Mission", desc: "To make logistics accessible, affordable, and transparent for every business and individual across India." },
              { icon: Eye, title: "Our Vision", desc: "To build the most trusted logistics network in South Asia, connecting every doorstep with precision and care." },
              { icon: Heart, title: "Our Values", desc: "Reliability, transparency, innovation, and customer-first thinking are at the heart of everything we do." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-8"
              >
                <div className="w-14 h-14 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-brand-500" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Story section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-xs font-bold uppercase tracking-widest text-brand-500 mb-2">Our Journey</p>
              <h2 className="font-heading text-4xl font-bold text-slate-900 uppercase mb-6">From Idea to Network</h2>
              <p className="text-slate-500 leading-relaxed mb-4">
                Sendtoo was founded with a simple idea: logistics shouldn't be complicated. We saw small businesses and individuals struggling with unreliable couriers, hidden fees, and zero visibility into their shipments.
              </p>
              <p className="text-slate-500 leading-relaxed mb-4">
                We built a platform that puts transparency first — real-time tracking, upfront pricing, and a network of verified agents who take pride in their work.
              </p>
              <p className="text-slate-500 leading-relaxed">
                Today, Sendtoo serves thousands of customers across 120+ cities, delivering not just packages but peace of mind.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-xl overflow-hidden shadow-xl"
            >
              <img
                src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?w=600&q=80"
                alt="Sendtoo team"
                className="w-full h-80 object-cover"
              />
            </motion.div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-500 mb-2">Meet the People</p>
            <h2 className="font-heading text-4xl font-bold text-slate-900 uppercase mb-12">Our Team</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {team.map((member, i) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-slate-50 rounded-lg p-6"
                >
                  <div className="w-16 h-16 bg-brand-500 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-heading font-bold text-2xl">
                    {member.name[0]}
                  </div>
                  <h4 className="font-semibold text-slate-800 text-sm">{member.name}</h4>
                  <p className="text-xs text-slate-500 mt-0.5">{member.role}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
